import { useState, useRef } from "react";
import { playNow, kickAudio } from "@/audio/piano";

/**
 * IntervalEarTrainer — the heart of the Ear Training course.
 *
 * Plays a random interval (harmonic or melodic) from a chosen vocabulary,
 * waits for the listener to guess, then reveals the answer. The four
 * "vibe" categories from Jason's notes (Resolution / Anticipation /
 * Mystery / Tension) are colour-coded so the student also internalises
 * the emotional fingerprint of each interval, not just its name.
 */

type IntervalDef = {
  name: string;        // "M3", "P5", "tritone"
  fullName: string;    // "Major 3rd", "Perfect 5th", "Tritone"
  semitones: number;
  vibe: "Resolution" | "Anticipation" | "Mystery" | "Tension";
};

const ALL_INTERVALS: IntervalDef[] = [
  { name: "Unison",  fullName: "Unison",        semitones: 0,  vibe: "Resolution" },
  { name: "m2",      fullName: "Minor 2nd",     semitones: 1,  vibe: "Tension" },
  { name: "M2",      fullName: "Major 2nd",     semitones: 2,  vibe: "Anticipation" },
  { name: "m3",      fullName: "Minor 3rd",     semitones: 3,  vibe: "Resolution" },
  { name: "M3",      fullName: "Major 3rd",     semitones: 4,  vibe: "Resolution" },
  { name: "P4",      fullName: "Perfect 4th",   semitones: 5,  vibe: "Anticipation" },
  { name: "Tritone", fullName: "Tritone (♯4/♭5)", semitones: 6, vibe: "Tension" },
  { name: "P5",      fullName: "Perfect 5th",   semitones: 7,  vibe: "Resolution" },
  { name: "m6",      fullName: "Minor 6th",     semitones: 8,  vibe: "Mystery" },
  { name: "M6",      fullName: "Major 6th",     semitones: 9,  vibe: "Mystery" },
  { name: "m7",      fullName: "Minor 7th",     semitones: 10, vibe: "Anticipation" },
  { name: "M7",      fullName: "Major 7th",     semitones: 11, vibe: "Tension" },
  { name: "Octave",  fullName: "Octave",        semitones: 12, vibe: "Resolution" },
];

const VIBE_COLOR: Record<IntervalDef["vibe"], string> = {
  Resolution:    "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  Anticipation:  "bg-sky-500/20 text-sky-200 border-sky-500/40",
  Mystery:       "bg-violet-500/20 text-violet-200 border-violet-500/40",
  Tension:       "bg-rose-500/20 text-rose-200 border-rose-500/40",
};

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
function midiToPitch(midi: number): string {
  const o = Math.floor(midi / 12) - 1;
  return NOTE_NAMES[midi % 12] + o;
}

interface IntervalEarTrainerProps {
  /** Restrict the vocabulary, e.g. ["P5", "P4", "M3", "m3"] for the first lesson */
  vocabulary?: string[];
}

export function IntervalEarTrainer({ vocabulary }: IntervalEarTrainerProps) {
  const intervals = vocabulary
    ? ALL_INTERVALS.filter((i) => vocabulary.includes(i.name))
    : ALL_INTERVALS;

  const [current, setCurrent] = useState<IntervalDef | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [mode, setMode] = useState<"harmonic" | "melodic">("melodic");
  const [score, setScore] = useState({ right: 0, wrong: 0 });
  const rootRef = useRef<number>(60); // start at C4

  const newQuestion = () => {
    kickAudio();
    const pick = intervals[Math.floor(Math.random() * intervals.length)];
    // Choose a random root in MIDI 55-67 (G3 to G4) so high+low intervals fit
    rootRef.current = 55 + Math.floor(Math.random() * 13);
    setCurrent(pick);
    setRevealed(false);
    void playInterval(pick);
  };

  const replay = () => {
    if (current) void playInterval(current);
  };

  const playInterval = async (intv: IntervalDef) => {
    const root = rootRef.current;
    const top = root + intv.semitones;
    const rootP = midiToPitch(root);
    const topP = midiToPitch(top);
    if (mode === "harmonic") {
      void playNow([rootP, topP], "1n", 0.7);
    } else {
      void playNow(rootP, "2n", 0.85);
      setTimeout(() => void playNow(topP, "2n", 0.85), 700);
    }
  };

  const guess = (intv: IntervalDef) => {
    if (!current || revealed) return;
    const correct = intv.name === current.name;
    setScore((s) => ({
      right: s.right + (correct ? 1 : 0),
      wrong: s.wrong + (correct ? 0 : 1),
    }));
    setRevealed(true);
  };

  return (
    <div className="my-5 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.04] to-zinc-900/[0.04] p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
            Interval ear trainer
          </div>
          <div className="mt-1 text-sm text-nsm-dim">
            Listen, guess the interval, then reveal. Re-roll for another. {intervals.length} intervals in this drill.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-full border border-zinc-700 bg-zinc-900/60 px-2 py-1 text-[11px] font-mono text-zinc-300">
            <span className="text-emerald-300">{score.right}</span>
            <span className="mx-1 text-zinc-500">/</span>
            <span className="text-rose-300">{score.wrong}</span>
          </div>
          <button
            onClick={() => setMode((m) => m === "melodic" ? "harmonic" : "melodic")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/20"
            title="Switch between melodic (one note then the other) and harmonic (both at once)"
          >
            {mode === "melodic" ? "♪ Melodic" : "❘❘ Harmonic"}
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {!current ? (
          <button
            onClick={newQuestion}
            className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-zinc-950 hover:bg-emerald-400"
          >
            ▶ Start
          </button>
        ) : (
          <>
            <button
              onClick={replay}
              className="rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/25"
            >
              ↻ Hear again
            </button>
            {!revealed ? (
              <button
                onClick={() => setRevealed(true)}
                className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm font-semibold text-zinc-300 hover:text-white"
              >
                Reveal
              </button>
            ) : null}
            <button
              onClick={newQuestion}
              className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-bold text-zinc-950 hover:bg-emerald-400"
            >
              Next
            </button>
          </>
        )}
      </div>

      {/* Answer chips — clicking one is your guess */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
        {intervals.map((intv) => {
          const isAnswer = revealed && current?.name === intv.name;
          return (
            <button
              key={intv.name}
              onClick={() => guess(intv)}
              disabled={!current || revealed}
              className={`rounded-lg border-2 px-2 py-2 text-left text-[12px] transition disabled:cursor-default ${
                isAnswer
                  ? `${VIBE_COLOR[intv.vibe]} ring-2 ring-emerald-300 scale-105`
                  : `border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:border-emerald-500/50 hover:text-white`
              }`}
            >
              <div className="font-mt text-base font-semibold">{intv.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{intv.fullName}</div>
            </button>
          );
        })}
      </div>

      {revealed && current && (
        <div className={`mt-4 rounded-lg border-2 p-3 text-sm ${VIBE_COLOR[current.vibe]}`}>
          <strong className="font-mt text-lg">{current.fullName}</strong> — vibe: <strong>{current.vibe}</strong>
          <span className="ml-2 text-[12px] opacity-80">({current.semitones} semitone{current.semitones !== 1 ? "s" : ""})</span>
        </div>
      )}
    </div>
  );
}
